import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { t } from 'ttag'
import { getLocation } from 'ducks/router'
import { useModal } from 'hooks'
import { candidates, jobs } from 'orm'

// one shared instance, so the memoized item is reused across renders
const getJob = jobs.getItem()

const useMoveCandidateModalQueries = () => {
  const dispatch = useDispatch()

  const handleFetchCandidates = React.useCallback(
    (
      { projectId, status },
      { limit, offset, ordering, search, is_favourite, hire_status },
    ) => {
      dispatch(
        candidates.fetchStart({
          project: projectId,
          limit,
          offset,
          ordering,
          search,
          status,
          is_favourite,
          hire_status,
        }),
      )
    },
    [dispatch],
  )

  return {
    handleFetchCandidates,
  }
}

const useMoveCandidateModalMutations = () => {
  const dispatch = useDispatch()

  const handleMoveCandidate = React.useCallback(
    (
      { data, id },
      {
        onFailureAction,
        onSuccessAction,
        postSuccessAction,
      }: {
        onFailureAction?: any
        onSuccessAction?: any
        postSuccessAction?: any
      } = {},
    ) => {
      dispatch(
        candidates.patchStart({
          pk: id,
          data,
          message: t`Status was successfully changed.`,
          onFailureAction,
          onSuccessAction,
          postSuccessAction,
        }),
      )
    },
    [dispatch],
  )

  return {
    handleMoveCandidate,
  }
}

export const useMoveCandidateModalState = () => {
  const {
    payload: { id },
  } = useSelector(getLocation)
  const position = useSelector(state => getJob(state, { pk: id }))

  return {
    state: { position },
  }
}

export const useMoveCandidateModal = (state, handlers) => {
  const { id, projectId, status } = state
  const { handleReset } = handlers

  const { query = {} } = useSelector(getLocation)

  const { handleClose, handleOpen, isOpen } = useModal()

  const { handleFetchCandidates } = useMoveCandidateModalQueries()

  const { handleMoveCandidate } = useMoveCandidateModalMutations()

  const handlePostSuccessAction = React.useCallback(() => {
    handleClose()
    handleReset()
    handleFetchCandidates({ projectId, status }, query)
  }, [
    projectId,
    query,
    status,
    handleClose,
    handleFetchCandidates,
    handleReset,
  ])

  const handleConfirm = React.useCallback(
    (data, { onError, onSuccess }: { onError?: any; onSuccess?: any } = {}) => {
      handleMoveCandidate(
        { data, id },
        {
          onFailureAction: onError,
          postSuccessAction: () => {
            handlePostSuccessAction()
            onSuccess()
          },
        },
      )
    },
    [id, handleMoveCandidate, handlePostSuccessAction],
  )

  return {
    handleMoveCandidateModalClose: handleClose,
    handleMoveCandidateModalConfirm: handleConfirm,
    handleMoveCandidateModalOpen: handleOpen,
    isMoveCandidateModalOpen: isOpen,
  }
}
